// Full-document render of the toppers OfferPage: PageSkeleton (the React
// document shell) wrapping the OfferPage layout DOM. The ``.offer-page__side-nav``
// mobile MENU toggle owns its own open/close state (``OfferPageMobileSideNav``).
// The ``|safe`` copy fields (headline / sub_headline / details / how-it-works /
// terms / legal) and the raw sibling titles are trusted CMS HTML, injected raw so
// entities pass through unescaped; the finance-banner text is autoescaped copy, so
// it renders as React text nodes.
import React, { useState } from "react";

import { templates } from "@reactivated";

import { OfferPageFaqDetails } from "../components/OfferPageFaqDetails";
import { OfferPagePricingCharts } from "../components/OfferPagePricingCharts";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader / SiteFooter chain via PageSkeleton, which is typed with
// mutable reactivated props. The body islands accept Immutable props, to which
// these are assignable.
type IProps = templates.OfferPageTemplate;

type ISibling = IProps["siblings"][number];

const renderSiblings = (siblings: readonly ISibling[]): React.JSX.Element[] =>
    siblings.map((sibling, i) => (
        <div className="accordion__list-item" key={`${sibling.url}-${i}`}>
            <a
                href={sibling.url}
                className={sibling.is_active ? "is-active" : undefined}
                dangerouslySetInnerHTML={{ __html: sibling.title }}
            />
        </div>
    ));

// Mobile MENU toggle for the offer-page side nav: reveals the sibling-offer
// list. The trigger is an `<a>` with no `href` (legacy relied on a global click
// handler), so it is exposed as a keyboard-operable `role="button"` with
// `aria-expanded`; it starts closed.
const OfferPageMobileSideNav = (props: {
    siblings: readonly ISibling[];
}): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => {
        setIsOpen((open) => !open);
    };
    return (
        <div className="accordion__list-item accordion__list-item--mobile">
            <a
                className={
                    isOpen
                        ? "category-list-side-nav__mobile-toggle accordion accordion--is-active"
                        : "category-list-side-nav__mobile-toggle accordion"
                }
                role="button"
                tabIndex={0}
                aria-expanded={isOpen ? "true" : "false"}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                menu
            </a>
            <div
                className={
                    isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
            >
                {renderSiblings(props.siblings)}
            </div>
        </div>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page, header_bg_url, background_bg_url, siblings } = props;
    // details + optional details_cta render as bare siblings inside one
    // ``.offer-page__details`` div (no per-field wrapper); how_it_works_cta is
    // unconditional (an empty CharField just appends "").
    const detailsHtml =
        props.details + (page.details_cta ? `\n${page.details_cta}` : "");
    const howItWorksHtml = `${props.how_it_works_details}\n${page.how_it_works_cta}`;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {header_bg_url !== null ? (
                <div
                    className="hero offer-page-hero l-full-width"
                    style={{ backgroundImage: `url(${header_bg_url})` }}
                />
            ) : null}

            <div className="offer-page__wrapper u-margin-bottom--xl">
                <div className="l-capped-width">
                    <div className="offer-page__side-nav">
                        <div className="accordion__list-item--desktop">
                            <div className="accordion__list-header">
                                Available Offers
                            </div>
                            {renderSiblings(siblings)}
                        </div>

                        <OfferPageMobileSideNav siblings={siblings} />
                    </div>

                    <div className="offer-page__content">
                        <div
                            className={`offer-page__copy${
                                background_bg_url !== null
                                    ? " offer-page__copy--hasbg"
                                    : ""
                            }`}
                        >
                            <div className="offer-page__headline">
                                <h1
                                    dangerouslySetInnerHTML={{
                                        __html: page.headline,
                                    }}
                                />
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: page.sub_headline,
                                    }}
                                />
                            </div>
                            <div
                                className="offer-page__details"
                                dangerouslySetInnerHTML={{
                                    __html: detailsHtml,
                                }}
                            />

                            {background_bg_url !== null ? (
                                <div className="offer-page__support-image">
                                    <span
                                        className="offer-page__image"
                                        style={{
                                            backgroundImage: `url(${background_bg_url})`,
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>

                        {props.how_it_works_details ? (
                            <div className="offer-page__how-it-works">
                                <div className="offer-page__headline">
                                    <h2>How it Works</h2>
                                </div>
                                <div
                                    className="offer-page__details"
                                    dangerouslySetInnerHTML={{
                                        __html: howItWorksHtml,
                                    }}
                                />
                            </div>
                        ) : null}

                        {page.pricing_charts.value.length > 0 ? (
                            <div className="offer-page__pricing">
                                <OfferPagePricingCharts page={page} />
                            </div>
                        ) : null}

                        {props.terms_details ? (
                            <div className="offer-page__terms">
                                <div className="offer-page__headline">
                                    <h2>Terms and Conditions</h2>
                                </div>
                                <div
                                    className="offer-page__details"
                                    dangerouslySetInnerHTML={{
                                        __html: props.terms_details,
                                    }}
                                />
                            </div>
                        ) : null}

                        {page.banner_message1_line1 ? (
                            <div className="offer-page__finance-banner">
                                <div className="col">
                                    <h2>{page.banner_message1_line1}</h2>
                                    <h3>{page.banner_message1_line2}</h3>
                                </div>
                                <div className="col">
                                    <h2>{page.banner_message2_line1}</h2>
                                    <h3>{page.banner_message2_line2}</h3>
                                </div>
                                <a
                                    href="/finance-your-purchase"
                                    title="Financing Details"
                                >
                                    See Financing Details
                                </a>
                            </div>
                        ) : null}

                        {page.faq_details.value.length > 0 ? (
                            <div className="offer-page__faq">
                                <div className="offer-page__headline">
                                    <h2>Frequently Asked Questions</h2>
                                </div>
                                <div className="block-accordion_section">
                                    <div className="accordion__container">
                                        <ul className="accordion__list">
                                            <OfferPageFaqDetails page={page} />
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {props.legal_details ? (
                            <div
                                className="offer-page__mousetype"
                                dangerouslySetInnerHTML={{
                                    __html: props.legal_details,
                                }}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </PageSkeleton>
    );
};
